import {colors} from 'shells/colors/colors';

import {
    type GetSessionUser_loggedInUser_sendableEmailAddresses as UserSendableEmailAddress,
    type GetSharedInboxConfig_sharedInboxConfig_sharedAddresses as SharedAddress,
    FromDomainStatus,
} from 'nutshell-graphql-types';

import {INTERNAL_NOTE_OPTION} from './consts';
import {MODE, type Mode, type SenderAddress, type SenderAddressOption} from './types';

export const getCantSendMessage = (
    cantSendStatus: string,
    recipientName?: string | null
): string | null | undefined => {
    switch (cantSendStatus) {
        case 'NOT_ENABLED':
            return 'Integration is not enabled, you cannot send any more messages in this conversation.';
        case 'NO_RBAC_PERMISSION':
            return 'You don’t have permission to send text messages in Nutshell. Contact your administrator if you believe this to be an error.';
        case 'NOT_ASSIGNED':
            return 'Oops! It seems you’re not assigned to this message thread, which means you’re unable to send messages here.';
        case 'CHATBOT_MANAGED':
            return 'This thread is being managed by the chatbot. Feel free to step in by assigning someone to the thread.';
        case 'THREAD_CLOSED_INBOX_PAGE':
            return `This thread has been closed. To start a new thread, please visit ${
                recipientName || 'this person'
            }’s page and click the ’Send a text’ button located above the timeline.`;
        case 'CHANNEL_BLOCKED':
            return 'This thread has been closed, you cannot send any more messages in this conversation.';
        case 'CHANNEL_INVALID':
            return `It looks like ${
                recipientName || 'this person'
            } has an invalid phone number, so you can’t send a message to them`;
        case 'CHANNEL_UNDELIVERABLE':
            return 'Messages to this number aren’t being delivered. No further messages can be sent.';
        case 'CHANNEL_MISSING_CONTACT_INFO':
            return `It looks like ${
                recipientName || 'this lead'
            } has no associated people or companies, so you can’t send a message to them`;
        case 'THREAD_CLOSED':
            return 'This thread has been closed, you cannot send any more messages in this conversation.';
        case 'MESSAGING_WINDOW_EXPIRED':
            return 'The 24-hour messaging window has expired. Instagram only allows replies within 24 hours of receiving a message from the customer.';
        case 'NOT_STARTED_NO_CREDITS':
            return 'It looks like Nutshell text messaging hasn’t been set up yet.';
        case 'IN_PROGRESS':
            return 'Your enrollment is currently in progress.';
        case 'ENROLLMENT_ERROR':
            return 'There is a problem with your enrollment.';
        case 'NO_INBOX_PLAN':
        case 'NO_REMAINING_CREDITS':
            return 'You have used all your text credits for the month.';
        case 'NOT_STARTED':
            return 'You have text credits available. To use your text credits, you need to complete your enrollment.';
        case 'NO_NUTSHELL_CHANNEL':
        case 'INVALID_NUTSHELL_CHANNEL':
        default:
            return null;
    }
};

export const getShouldHideControls = (cantSendStatus: string): boolean => {
    const hideControlsButtonStatuses = [
        'NOT_ASSIGNED',
        'CHATBOT_MANAGED',
        'THREAD_CLOSED_SIDEBAR',
        'THREAD_CLOSED_INBOX_PAGE',
        'CHANNEL_BLOCKED',
        'CHANNEL_INVALID',
        'CHANNEL_UNDELIVERABLE',
        'CHANNEL_MISSING_CONTACT_INFO',
        'NO_RBAC_PERMISSION',
        'MESSAGING_WINDOW_EXPIRED',
    ];

    return hideControlsButtonStatuses.includes(cantSendStatus);
};

export const getSenderAddressOptions = (
    senderAddresses: SenderAddress[]
): SenderAddressOption[] => {
    const options = senderAddresses.map((address) => ({
        value: address.address,
        label: address.display,
        isDisabled: address.isDisabled,
    }));

    return [INTERNAL_NOTE_OPTION, ...options];
};

export const getSenderAddressStyles = (mode: Mode) => {
    return {
        // @ts-expect-error
        control: (base) => ({
            ...base,
            border: 'none',
            borderRadius: '8px 8px 0 0',
            boxShadow: 'none',
            minWidth: '120px',
            '&:hover': {border: 'none'},
            backgroundColor: mode === MODE.INTERNAL_NOTE ? colors.tagYellowLt : colors.white,
            padding: '4px 0 0 4px',
            flexWrap: 'nowrap',
        }),
        // @ts-expect-error
        singleValue: (base) => ({
            ...base,
            position: 'static',
            transform: 'none',
            maxWidth: 'none',
            overflow: 'visible',
        }),
        // @ts-expect-error
        valueContainer: (base) => ({
            ...base,
            flexWrap: 'nowrap',
            overflow: 'visible',
            paddingRight: 0,
        }),
        // @ts-expect-error
        dropdownIndicator: (base) => ({
            ...base,
            paddingLeft: 4,
        }),
    };
};

export const mergeAndFlattenSenderAddresses = (
    userSendableEmailAddresses: UserSendableEmailAddress[] | null | undefined,
    sharedAddresses: SharedAddress[] | null | undefined
): SenderAddress[] => {
    return [
        ...(sharedAddresses?.map((address) => ({
            address: address.emailAddress.address,
            display: address.emailAddress.formatted || '',
            isDisabled:
                address.fromDomain?.nutshellDkimStatus !== FromDomainStatus.STATUS_VERIFIED ||
                address.fromDomain?.nutshellMailfromStatus !== FromDomainStatus.STATUS_VERIFIED ||
                address.fromDomain?.refreshedDmarcStatus !== FromDomainStatus.STATUS_VERIFIED,
        })) ?? []),
        ...(userSendableEmailAddresses?.map((email) => ({
            address: email.address,
            display: email.formatted || '',
        })) ?? []),
    ];
};
